import { api } from '../services/apiClient';
import {
  pullRunningHubFeatureHistoryPayloads,
  syncRunningHubHistoryItemToCloud,
} from './runningHubCloudHistory';

export function isStableHttpsImageUrl(raw: string | null | undefined): boolean {
  const u = (raw || '').trim();
  return /^https?:\/\//i.test(u) && !/^data:/i.test(u);
}

export function isInlineDataImageUrl(raw: string | null | undefined): boolean {
  return /^data:image\//i.test((raw || '').trim());
}

/** 历史条目可用原图：须与结果不同，且为 https 或当前会话内 data:image */
export function resolveUsableHistoryOriginalImage(item: {
  originalImage?: string;
  upscaledUrl?: string;
}): string | null {
  const result = (item.upscaledUrl || '').trim();
  const raw = (item.originalImage || '').trim();
  if (!raw || raw === result) return null;
  if (isStableHttpsImageUrl(raw) || isInlineDataImageUrl(raw)) return raw;
  return null;
}

/** 从 RunningHub job 云端归档（clientItemId 主键）读取已转 COS 的 originalImage */
export async function fetchPhysicalArchivedOriginalImageUrl(
  clientItemId: string,
  isAuthenticated: boolean,
): Promise<string | undefined> {
  if (!isAuthenticated || !clientItemId.trim()) return undefined;
  const pull = await pullRunningHubFeatureHistoryPayloads<{ id: string; originalImage?: string }>(
    'physical_reconstruct',
    isAuthenticated,
  );
  const row =
    pull.items.find((x) => x.id === `${clientItemId.trim()}-0`) ??
    pull.items.find((x) => x.id === clientItemId.trim());
  const o = typeof row?.originalImage === 'string' ? row.originalImage.trim() : '';
  if (isStableHttpsImageUrl(o)) return o;
  return undefined;
}

export type PersistPhysicalOriginalImageOptions = {
  historyArchived?: boolean;
  /** 已登录且非 job 归档时：先 sync 首条历史（含 base64 originalImage），走 runninghub-physical COS 通路 */
  firstHistoryItem?: Record<string, unknown>;
  userEmailForLog?: string | null;
  currentUserEmail?: string | null;
};

/**
 * 生成完成写入历史前：将 base64 原图转为稳定 HTTPS URL（runninghub-physical）。
 * 已是 https 则原样返回；未登录或 COS 失败时回退为传入值（localStorage 会 strip data:image）。
 */
export async function persistPhysicalOriginalImageUrl(
  activeImage: string,
  isAuthenticated: boolean,
  clientItemId: string,
  opts?: PersistPhysicalOriginalImageOptions,
): Promise<string> {
  const trimmed = activeImage.trim();
  if (isStableHttpsImageUrl(trimmed)) return trimmed;
  if (!isInlineDataImageUrl(trimmed)) return trimmed;
  if (!isAuthenticated || !clientItemId.trim()) return trimmed;

  if (opts?.historyArchived) {
    const archived = await fetchPhysicalArchivedOriginalImageUrl(clientItemId, isAuthenticated);
    if (archived) return archived;
  }

  if (!opts?.historyArchived && opts?.firstHistoryItem && typeof opts.firstHistoryItem.id === 'string') {
    const sync = await syncRunningHubHistoryItemToCloud(
      'physical_reconstruct',
      isAuthenticated,
      opts.firstHistoryItem.id,
      opts.firstHistoryItem,
      {
        userEmailForLog: opts.userEmailForLog,
        currentUserEmail: opts.currentUserEmail ?? opts.userEmailForLog,
      },
    );
    const fromSync = typeof sync.originalImage === 'string' ? sync.originalImage.trim() : '';
    if (isStableHttpsImageUrl(fromSync)) return fromSync;
  }

  try {
    const res = (await api.upsertRunningHubFeatureHistory({
      featureType: 'physical_reconstruct',
      clientItemId: `${clientItemId}__orig`,
      payload: {
        id: `${clientItemId}__orig`,
        originalImage: trimmed,
        timestamp: Date.now(),
      },
    })) as { originalImage?: string };
    const url = typeof res?.originalImage === 'string' ? res.originalImage.trim() : '';
    if (isStableHttpsImageUrl(url)) return url;
  } catch (e) {
    console.warn('[physical-original] inline COS persist failed', e);
  }

  return trimmed;
}
